import { lazy, Suspense, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import ToastBootstrap from "@/components/ToastBootstrap";
import { AuthProvider } from "@/context/AuthContext";
import { CrmProvider } from "@/context/CrmContext";
import { SessionProvider } from "@/context/SessionContext";
import { ProductsProvider } from "@/context/ProductsContext";
import { BasketProvider } from "@/context/BasketContext";
import Navbar from "@/components/Navbar";
import MarketSeo from "@/components/MarketSeo";
import DeferredSection from "@/components/DeferredSection";
import { useSession } from "@/context/SessionContext";
import { marketFromCountryId } from "@/lib/marketRoutes";
import { qrPathForCode } from "@/lib/qrRouting";
import { AgentInfoBanner } from "@/pages/AgentPage";

const Footer = lazy(() => import("@/components/Footer"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const BasketPage = lazy(() => import("@/pages/BasketPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const OrderThanksPage = lazy(() => import("@/pages/OrderThanksPage"));
const AgentPage = lazy(() => import("@/pages/AgentPage"));
const MsdsPage = lazy(() => import("@/pages/MsdsPage"));
const ReferralPage = lazy(() => import("@/pages/ReferralPage"));
const CalculatorPage = lazy(() => import("@/pages/CalculatorPage"));
const RefillPumpPage = lazy(() => import("@/pages/RefillPumpPage"));
const DosagePage = lazy(() => import("@/pages/DosagePage"));
const SimpleFaqPage = lazy(() => import("@/pages/SimpleFaqPage"));
const CustomerAccessPage = lazy(() => import("@/pages/CustomerAccessPage"));
const MarketOutlet = lazy(() => import("@/components/MarketOutlet"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminInvoices = lazy(() => import("@/pages/admin/AdminInvoices"));
const AdminQrCodes = lazy(() => import("@/pages/admin/AdminQrCodes"));
const AdminMetaPixelTest = lazy(() => import("@/pages/admin/AdminMetaPixelTest"));
const AdminFileRoute = lazy(() => import("@/pages/admin/AdminFileRoute"));
const AdminCrmUsers = lazy(() => import("@/pages/admin/AdminCrmUsers"));
const CrmLogin = lazy(() => import("@/pages/crm/CrmLogin"));
const CrmLayout = lazy(() => import("@/pages/crm/CrmLayout"));
const CrmCustomers = lazy(() => import("@/pages/crm/CrmCustomers"));
const CrmCustomerDetail = lazy(() => import("@/pages/crm/CrmCustomerDetail"));
const CrmImport = lazy(() => import("@/pages/crm/CrmImport"));
const CrmBulkEmail = lazy(() => import("@/pages/crm/CrmBulkEmail"));
const CrmHistory = lazy(() => import("@/pages/crm/CrmHistory"));

function RouteLoading() {
  return <main data-testid="route-loading" aria-busy="true" className="min-h-[40vh]" />;
}

function ScrollReset() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RootLanding() {
  return <main data-testid="root-market-resolving" aria-busy="true" className="min-h-screen" />;
}

function MarketHome() {
  const { agentMode } = useSession();
  return agentMode ? <AgentPage /> : <HomePage />;
}

const isStep09LandingPath = (pathname) => (
  pathname === "/"
  || /^\/(dk|se|gb)\/?$/i.test(pathname)
  || /^\/r\/[^/]+\/?$/i.test(pathname)
);

function Step09Router() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const routedRef = useRef(false);
  const { ready, countryId, qrCode, referralCode, campaignActive, utmCampaignCode } = useSession();

  useEffect(() => {
    if (!ready || routedRef.current || !isStep09LandingPath(pathname)) return;
    const market = marketFromCountryId(countryId);
    if (!market) return;
    const route = (target) => {
      const [targetPath, targetHash = ""] = target.split("#");
      const destination = `${targetPath}${search}${targetHash ? `#${targetHash}` : ""}`;
      routedRef.current = true;
      if (pathname !== targetPath || (targetHash && window.location.hash !== `#${targetHash}`)) {
        navigate(destination, { replace: true });
      }
    };

    // B: QR stopper kun videre routing. Step 07's rabat/badge-berettigelse
    // er allerede beregnet i SessionContext og bevares på QR-destinationen.
    const qrPath = qrPathForCode(qrCode);
    if (qrPath) {
      route(`/${market}${qrPath}`);
      return;
    }

    // Ugyldige/brugte referral-links skal fortsat vise ReferralPage's forklaring.
    if (/^\/r\/[^/]+\/?$/i.test(pathname)) {
      routedRef.current = true;
      return;
    }

    // C: Referral-siden bruger den allerede validerede kode fra Step 05.
    if (referralCode !== "None") {
      const referralTarget = `/r/${encodeURIComponent(referralCode)}`;
      const params = new URLSearchParams(search);
      if (![...params.keys()].some((key) => key.toLowerCase() === "countryid")) params.set("countryId", countryId);
      routedRef.current = true;
      navigate(`${referralTarget}?${params.toString()}`, { replace: true });
      return;
    }

    // D: Uden QR/referral er Step 06 + Step 07 den fælles kampagne-gate
    // for standardlandingen på 25L-produktet. Badget aktiveres ikke her.
    if (campaignActive && String(utmCampaignCode || "NONE").toUpperCase() !== "NONE") {
      route(`/${market}/produkter/invictusa-25l-tromle`);
      return;
    }

    // E: Evalueret markedets forside.
    route(`/${market}/`);
  }, [campaignActive, countryId, navigate, pathname, qrCode, ready, referralCode, search, utmCampaignCode]);

  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { agentMode, fallbackMarket, fallbackPath, ready, adsConfig } = useSession();
  const isAdmin = pathname.startsWith("/admin");
  const isRootLanding = pathname === "/";
  const isMarketHomepage = /^\/(dk|se|gb)\/?$/i.test(pathname);
  const keepsInitialHeroShell = isRootLanding || isMarketHomepage;
  useEffect(() => {
    if (!ready || !fallbackMarket || fallbackPath !== pathname || isRootLanding || pathname.startsWith(`/${fallbackMarket}/`)) return;
    navigate(`/${fallbackMarket}/`, { replace: true });
  }, [fallbackMarket, fallbackPath, isRootLanding, navigate, pathname, ready]);
  useEffect(() => {
    if (!keepsInitialHeroShell) document.getElementById("initial-hero-shell")?.remove();
  }, [keepsInitialHeroShell]);
  // Google Ads-tagget konfigureres af det offentlige bootstrap-svar.
  useEffect(() => {
    if (isAdmin || !ready) return undefined;
    let active = true;
    const start = () => import("@/lib/googleAds").then(({ initGoogleAds }) => {
      if (active) initGoogleAds(adsConfig);
    });
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(start, { timeout: 2000 })
      : window.setTimeout(start, 1200);
    return () => {
      active = false;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, [adsConfig, isAdmin, ready]);
  useEffect(() => {
    if (agentMode && pathname !== "/" && pathname !== "/gb/" && pathname !== "/agent") {
      sessionStorage.setItem("invictus_agent_notice_dismissed", "true");
    }
  }, [agentMode, pathname]);
  if (!isAdmin && !ready) return <RouteLoading />;
  return (
    <>
      <ScrollReset />
      <MarketSeo />
      {!isAdmin && !isRootLanding && <Navbar />}
      {!isAdmin && agentMode && !isMarketHomepage && <AgentInfoBanner />}
      {!isAdmin && <Step09Router />}
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/" element={<RootLanding />} />
        <Route path="/produkter" element={<Navigate to="/#emballager" replace />} />
        <Route path="/produkter/:slug" element={<ProductDetailPage />} />
        <Route path="/kurv" element={<BasketPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/ordre/tak" element={<OrderThanksPage />} />
        <Route path="/kunde/aktiver" element={<CustomerAccessPage />} />
        <Route path="/kunde/login" element={<CustomerAccessPage />} />
        <Route path="/agent" element={<Navigate to="/gb/" replace />} />
        <Route path="/msds" element={<MsdsPage />} />
        <Route path="/r/:code" element={<ReferralPage />} />
        <Route path="/calculator" element={<CalculatorPage />} />
        <Route path="/paafyldning-metalpumpe" element={<RefillPumpPage pump="metal" />} />
        <Route path="/paafyldning-plastpumpe" element={<RefillPumpPage pump="plast" />} />
        <Route path="/hvor-meget-skal-mit-daek" element={<DosagePage />} />
        <Route path="/:market" element={<MarketOutlet />}>
          <Route index element={<MarketHome />} />
          <Route path="produkter" element={<Navigate to="../#emballager" replace />} />
          <Route path="produkter/:slug" element={<ProductDetailPage />} />
          <Route path="kurv" element={<BasketPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="ordre/tak" element={<OrderThanksPage />} />
          <Route path="kunde/aktiver" element={<CustomerAccessPage />} />
          <Route path="kunde/login" element={<CustomerAccessPage />} />
          <Route path="msds" element={<MsdsPage />} />
          <Route path="calculator" element={<CalculatorPage />} />
          <Route path="paafyldning-metalpumpe" element={<RefillPumpPage pump="metal" />} />
          <Route path="paafyldning-plastpumpe" element={<RefillPumpPage pump="plast" />} />
          <Route path="hvor-meget-skal-mit-daek" element={<DosagePage />} />
          <Route path="kendte-sporgsmaal-virkemaade" element={<SimpleFaqPage testid="page-virkemaade" titleKey="faq.virkemaade.title" itemsKey="faq.virkemaade.items" />} />
          <Route path="bestilling-og-levering" element={<SimpleFaqPage testid="page-bestilling" titleKey="faq.bestilling.title" itemsKey="faq.bestilling.items" />} />
        </Route>
        {/* /dk/-aliasser: QR-koder er genereret med markeds-præfiks — de skal virke uden omprint.
            Samme indhold som de danske sider, indtil den rigtige markedsstruktur (dk/se) bygges */}
        <Route path="/dk/paafyldning-metalpumpe" element={<RefillPumpPage pump="metal" />} />
        <Route path="/dk/paafyldning-plastpumpe" element={<RefillPumpPage pump="plast" />} />
        <Route path="/dk/hvor-meget-skal-mit-daek" element={<DosagePage />} />
        <Route
          path="/kendte-sporgsmaal-virkemaade"
          element={
            <SimpleFaqPage
              testid="page-virkemaade"
              titleKey="faq.virkemaade.title"
              itemsKey="faq.virkemaade.items"
            />
          }
        />
        <Route
          path="/bestilling-og-levering"
          element={
            <SimpleFaqPage
              testid="page-bestilling"
              titleKey="faq.bestilling.title"
              itemsKey="faq.bestilling.items"
            />
          }
        />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/crm/login" element={<CrmLogin />} />
        <Route path="/admin/crm" element={<CrmLayout />}>
          <Route index element={<Navigate to="/admin/crm/kunder" replace />} />
          <Route path="kunder" element={<CrmCustomers />} />
          <Route path="kunder/:id" element={<CrmCustomerDetail />} />
          <Route path="import" element={<CrmImport />} />
          <Route path="masse-email" element={<CrmBulkEmail />} />
          <Route path="historik" element={<CrmHistory />} />
        </Route>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/produkter" replace />} />
          <Route path="crm-brugere" element={<AdminCrmUsers />} />
          <Route path="produkter" element={<AdminProducts />} />
          <Route path="ordrer" element={<AdminOrders />} />
          <Route path="parametre" element={<AdminFileRoute fileKey="parameters" />} />
          <Route path="siteparametre" element={<AdminFileRoute fileKey="site_parameters" />} />
          <Route path="landeindhold" element={<AdminFileRoute fileKey="country_content" />} />
          <Route path="produkttekster" element={<AdminFileRoute fileKey="product_texts" />} />
          <Route path="kunder" element={<AdminFileRoute fileKey="customers" />} />
          <Route path="ordrelinjer" element={<AdminFileRoute fileKey="orderlines" />} />
          <Route path="fakturaer" element={<AdminInvoices />} />
          <Route path="evalueringer" element={<AdminFileRoute fileKey="evaluations" />} />
          <Route path="enheder" element={<AdminFileRoute fileKey="devices" />} />
          <Route path="qr-koder" element={<AdminQrCodes />} />
          <Route path="meta-pixel-test" element={<AdminMetaPixelTest />} />
          <Route path="henvisninger" element={<AdminFileRoute fileKey="referrals" />} />
        </Route>
      </Routes>
      </Suspense>
      {!isAdmin && !isRootLanding && <DeferredSection testId="deferred-footer" placeholderClassName="min-h-[52.875rem] md:min-h-[24.125rem]" activateAfterScroll><Footer /></DeferredSection>}
    </>
  );
}

function App() {
  useEffect(() => {
    let active = true;
    let lenis = null;
    let rafId;
    const start = () => import("lenis").then(({ default: Lenis }) => {
      if (!active) return;
      lenis = new Lenis({ lerp: 0.09 });
      window.__lenis = lenis;
      const raf = (time) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    });
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(start, { timeout: 2000 })
      : window.setTimeout(start, 1200);
    return () => {
      active = false;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
      if (rafId) cancelAnimationFrame(rafId);
      lenis?.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App bg-[#0B0E12] min-h-screen text-slate-100">
      <div className="grain-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <CrmProvider>
          <SessionProvider>
            <ProductsProvider>
              <BasketProvider>
                <Shell />
              </BasketProvider>
            </ProductsProvider>
          </SessionProvider>
          </CrmProvider>
        </AuthProvider>
      </BrowserRouter>
      <ToastBootstrap />
    </div>
  );
}

export default App;
